import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-reimagined/components/notifications';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';

type Transition = ReturnType<RouterService['transitionTo']>;

export default class AuthenticatedTasDashboardRoute extends Route {
  @service declare sessionContext: SessionContextService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare tuitionAssistance: TuitionAssistanceService;

  // check for mentorship form submission re-route without passing
  // as a param that needs to be reset to prevent persistence
  beforeModel(transition: Transition) {
    // The main TAS dashboard always uses live data — clear any
    // leftover demo mode that was set on the reimagined preview routes
    localStorage.removeItem('reimagined-demo-mode');

    if (transition.to?.queryParams.formSubmitted) {
      const successMessage = this.intl.t('tuition_assistance.mentoring.submit_success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    }
  }

  async model() {
    const currentEmployee = this.sessionContext.currentEmployee;

    const [tasData, tasUsedAmount, dataSourceInfo] = await Promise.all([
      this.reimaginedData.fetchTasData(),
      this.reimaginedData.fetchTasUsedAmount(),
      this.reimaginedData.getDataSourceInfo(),
    ]);

    // Compute enrolled template IDs from the user's applications
    const enrolledTemplateIds = new Set(
      tasData.applications
        .filter((app) => app.programTemplateId)
        .map((app) => app.programTemplateId as string)
    );

    const [eligibleTemplates, limitsStatus] = await Promise.all([
      this.reimaginedData.fetchEligibleTemplates(enrolledTemplateIds),
      currentEmployee
        ? this.tuitionAssistance.getV2LimitsStatus(currentEmployee, undefined)
        : undefined,
    ]);

    return {
      tasData,
      tasUsedAmount,
      dataSourceInfo,
      eligibleTemplates,
      limitsStatus,
    };
  }
}
